import {Injectable, Injector} from "@angular/core";
import {BehaviorSubject, firstValueFrom, Observable, from, of, forkJoin} from "rxjs";
import {tap, shareReplay, catchError, map} from "rxjs/operators";
import * as Models from "app/services/proxy/model/models";
import {APP_DOMAIN_CONTEXT_TOKEN} from "app/services/domain/app-domain-context.token";
import type {AppDomainContext} from "app/services/domain/domain.module";
import type {CloudAccountInsightsCollection} from "app/account-governance/shared/account-types/account-types.component";
import {ServiceAccountCountLookup} from "app/services/domain/service-accounts/service-account-count-lookup";

export interface CloudAccountInsightData
{
    cloudProvider: Models.CloudServiceName;
    insightCollection: CloudAccountInsightsCollection;
    collection: any;
}

interface CacheEntry<T>
{
    data$: Observable<T>;
    timestamp: number;
}

class ObservableCache<T>
{
    private m_cache      = new Map<string, CacheEntry<T>>();
    private m_inFlight   = new Map<string, Observable<T>>();
    private readonly TTL = 5 * 60 * 1000;

    private m_subject$ = new BehaviorSubject<T | null>(null);
    private m_loading$ = new BehaviorSubject<boolean>(false);
    private m_error$   = new BehaviorSubject<Error | null>(null);

    public readonly data$    = this.m_subject$.asObservable();
    public readonly loading$ = this.m_loading$.asObservable();
    public readonly error$   = this.m_error$.asObservable();

    public isFresh(key: string): boolean
    {
        const entry = this.m_cache.get(key);
        return !!entry && (Date.now() - entry.timestamp) < this.TTL;
    }

    public getOrCreate(key: string,
                       factory: () => Observable<T>): Observable<T>
    {
        this.cleanupExpired();
        const entry = this.m_cache.get(key);
        if (entry && (Date.now() - entry.timestamp) < this.TTL)
        {
            return entry.data$;
        }

        const inflight = this.m_inFlight.get(key);
        if (inflight) return inflight;

        this.m_loading$.next(true);
        this.m_error$.next(null);

        const data$ = factory()
            .pipe(
                tap(result =>
                    {
                        if (this.m_subject$.value !== result)
                        {
                            this.m_subject$.next(result);
                        }
                        this.m_loading$.next(false);
                        this.m_inFlight.delete(key);
                        this.m_cache.set(key,
                                         {
                                             data$,
                                             timestamp: Date.now()
                                         });
                    }),
                catchError(error =>
                           {
                               this.m_error$.next(error);
                               this.m_loading$.next(false);
                               this.m_inFlight.delete(key);
                               throw error;
                           }),
                shareReplay({
                                bufferSize: 1, //
                                refCount  : true
                            })
            );

        this.m_inFlight.set(key, data$);
        return data$;
    }

    private cleanupExpired(): void
    {
        const now = Date.now();
        for (const [key, entry] of this.m_cache.entries())
        {
            if (now - entry.timestamp >= this.TTL)
            {
                this.m_cache.delete(key);
            }
        }
    }

    public invalidate(key: string): void
    {
        this.m_cache.delete(key);
        this.m_inFlight.delete(key);
    }

    public clear(): void
    {
        this.m_cache.clear();
        this.m_inFlight.clear();
        this.m_subject$.next(null);
        this.m_loading$.next(false);
        this.m_error$.next(null);
    }

    public getCurrent(): T | null
    {
        return this.m_subject$.value;
    }
}

@Injectable({providedIn: "root"})
export class ServiceAccountSummaryStore
{
    private summaryCache   = new ObservableCache<Array<Models.ServiceAccountSummary>>();
    private dashboardCache = new ObservableCache<CloudAccountInsightData[]>();
    private listCache      = new ObservableCache<Models.ServiceAccountListResponse>();

    // Public streams for summary
    public readonly summary$ = this.summaryCache.data$;
    public readonly loading$ = this.summaryCache.loading$;
    public readonly error$   = this.summaryCache.error$;

    // Public streams for dashboard
    public readonly dashboardInsights$ = this.dashboardCache.data$;
    public readonly dashboardLoading$  = this.dashboardCache.loading$;
    public readonly dashboardError$    = this.dashboardCache.error$;

    private _app: AppDomainContext;

    constructor(private injector: Injector) {}

    private get app(): AppDomainContext
    {
        if (!this._app) this._app = this.injector.get(APP_DOMAIN_CONTEXT_TOKEN);
        return this._app;
    }

    private getCacheKey(filter: Models.ServiceAccountFilter): string
    {
        const key = this.stableStringify(filter);
        return key;
    }

    private stableStringify(obj: any): string
    {
        if (Array.isArray(obj))
        {
            if (obj.length === 0) return undefined;
            return `[${obj.map(v => this.stableStringify(v))
                          .join(",")}]`;
        }
        else if (obj !== null && typeof obj === "object")
        {
            const entries = Object.keys(obj)
                                  .sort()
                                  .filter(k => obj[k] !== undefined && obj[k] !== null && !(Array.isArray(obj[k]) && obj[k].length === 0))
                                  .map(k => `${JSON.stringify(k)}:${this.stableStringify(obj[k])}`);
            return `{${entries.join(",")}}`;
        }
        else
        {
            return JSON.stringify(obj);
        }
    }


    public loadSummary(filter: Models.ServiceAccountFilter): Observable<Array<Models.ServiceAccountSummary>>
    {
        const key = this.getCacheKey(filter);
        return this.summaryCache.getOrCreate(
            key,
            () => from(this.app.serviceAccountService.getSummary(filter))
        );
    }

    public fetchSummary(filter: Models.ServiceAccountFilter): Promise<Models.ServiceAccountSummary[]>
    {
        return firstValueFrom(this.loadSummary(filter));
    }

    public fetchOrganizedSummary(filter: Models.ServiceAccountFilter): Promise<ServiceAccountCountLookup>
    {
        return this.fetchSummary(filter)
                   .then(summaries => new ServiceAccountCountLookup(filter, summaries));
    }

    public invalidate(filter: Models.ServiceAccountFilter): void
    {
        const key = this.getCacheKey(filter);
        this.summaryCache.invalidate(key);
    }

    public clearCache(): void
    {
        this.summaryCache.clear();
    }

    private getListCacheKey(request: Models.ServiceAccountListRequest): string
    {
        return `list|${this.stableStringify(request)}`;
    }

    public loadList(
        request: Models.ServiceAccountListRequest,
        fetcher: (req: Models.ServiceAccountListRequest) => Promise<Models.ServiceAccountListResponse>
    ): Observable<Models.ServiceAccountListResponse>
    {
        const key = this.getListCacheKey(request);
        return this.listCache.getOrCreate(
            key,
            () => from(fetcher(request))
        );
    }

    public invalidateList(request: Models.ServiceAccountListRequest): void
    {
        this.listCache.invalidate(this.getListCacheKey(request));
    }

    public clearListCache(): void
    {
        this.listCache.clear();
    }

    public loadDashboardInsights(cloudServices: Array<{ name: Models.CloudServiceName, collection: any }>,
                                 tenantId: string): Observable<CloudAccountInsightData[]>
    {

        return this.dashboardCache.getOrCreate(`dashboard|${tenantId}|${this.stableStringify(cloudServices.map(cloudname => cloudname.name))}`,
                                               () => this.createDashboardInsightsObservable(cloudServices, tenantId));
    }

    private createDashboardInsightsObservable(cloudServices: Array<{ name: Models.CloudServiceName, collection: any }>,
                                              tenantId: string): Observable<CloudAccountInsightData[]>
    {
        const insightObservables = cloudServices.map(service =>
                                                     {
                                                         const insightData: CloudAccountInsightData = {
                                                             cloudProvider    : service.name,
                                                             insightCollection: null,
                                                             collection       : service.collection
                                                         };

                                                         return from(this.populateInsightCollection(insightData, tenantId))
                                                             .pipe(
                                                                 map(() => insightData),
                                                                 catchError(error =>
                                                                            {
                                                                                console.error(`Error loading ${service.name}:`, error);
                                                                                return of(insightData);
                                                                            })
                                                             );
                                                     });

        return forkJoin(insightObservables);
    }

    private async populateInsightCollection(data: CloudAccountInsightData,
                                            tenantId: string): Promise<void>
    {
        const collection = new data.collection(this.app, tenantId, this);
        await collection.populateCollection();
        data.insightCollection = collection;
    }

    public refreshDashboardInsights(cloudServices: Array<{ name: Models.CloudServiceName, collection: any }>,
                                    tenantId: string): Observable<CloudAccountInsightData[]>
    {
        this.clearDashboardInsights();
        return this.loadDashboardInsights(cloudServices, tenantId);
    }

    public clearDashboardInsights(): void
    {
        this.dashboardCache.clear();
    }

    public getCurrentDashboardInsights(): CloudAccountInsightData[] | null
    {
        return this.dashboardCache.getCurrent();
    }
}
